import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
} from '@mui/material';
import { ContextSession } from "../../services/ContextSession";
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/es';

// Configuración de localización
const localizer = momentLocalizer(moment);

const ProximasAudiencias = () => {
  const [audiencias, setAudiencias] = useState({});
  const [demandas, setDemandas] = useState([]);
  const [procuradores, setProcuradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDemandas, setLoadingDemandas] = useState(false);
  const [filtroProcurador, setFiltroProcurador] = useState('');
  const [viewMode, setViewMode] = useState('lista');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina, setElementosPorPagina] = useState(3);
  const navigate = useNavigate();
  let sessi = useContext(ContextSession);

  // Obtener ID del usuario actual
  const usuarioActualId = useMemo(() => {
    return sessi?.sessionData?.id || null;
  }, [sessi]);

  // Agregar esta función para determinar roles
  const determinarRolUsuario = useCallback(() => {
    if (!sessi?.sessionData?.access) return 'desconocido';
    
    const modulos = sessi.sessionData.access;
    
    if (modulos.some(modulo => modulo.rol === "LegalJefe")) {
      return "LegalJefe";
    } else if (modulos.some(modulo => modulo.rol === "LegalProcuradorTGU")) {
      return "LegalProcuradorTGU";
    } else {
      return "desconocido";
    }
  }, [sessi]);

  // Memoizar los permisos
  const rolUsuario = useMemo(() => determinarRolUsuario(), [determinarRolUsuario]);
  const esUsuarioLegalJefe = useMemo(() => rolUsuario === "LegalJefe", [rolUsuario]);
  const esLegalProcuradorTGU = useMemo(() => rolUsuario === "LegalProcuradorTGU", [rolUsuario]);

  // Helper function para formatear fechas
  function formatFecha(fechaStr) {
    if (!fechaStr) return 'Fecha no definida';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const [year, month, day] = fechaStr.split('-');
    const fechaLocal = new Date(year, month - 1, day);
    return fechaLocal.toLocaleDateString('es-HN', options);
  }

  function calcularDiasRestantes(fechaStr) {
    if (!fechaStr) return 'Fecha no definida';
    const hoy = new Date();
    const fechaAud = new Date(fechaStr);
    const diffTime = fechaAud - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Vence Mañana';
    if (diffDays < 0) return `${Math.abs(diffDays)} días atrás`;
    return `Vence En ${diffDays} días`;
  }

  // Normalizar datos de procuradores
  const normalizeProcuradores = (procuradoresData) => {
    const normalized = {};
    procuradoresData.forEach(procurador => {
      normalized[procurador.id] = procurador;
      normalized[procurador.nombre] = procurador;
    });
    return normalized;
  };

  // Procesar datos de audiencias con filtro por rol
  const processAudienciasData = (audienciasData, procuradoresNormalized) => {
    const processed = {};
    
    for (const [key, auds] of Object.entries(audienciasData)) {
      const procurador = procuradoresNormalized[key];
      
      if (!procurador) {
        console.warn(`Procurador no encontrado: ${key}`);
        continue;
      }

      // Si es LegalProcuradorTGU, solo mostrar sus propias audiencias
      if (esLegalProcuradorTGU && procurador.id !== usuarioActualId) {
        continue;
      }
      
      if (!processed[procurador.id]) {
        processed[procurador.id] = {
          procurador,
          audiencias: []
        };
      }
      
      processed[procurador.id].audiencias.push(...auds);
    }
    
    return processed;
  };

  // Preparar eventos para React Big Calendar con filtro por rol
  const prepareCalendarEvents = (datosFiltrados, demandasData) => {
    const eventos = [];

    // Agregar fechas máximas de contestación
    datosFiltrados.forEach(audiencia => {
      let backgroundColor;
      let tipo = 'contestacion';
      
      switch(audiencia.estado?.toLowerCase()) {
        case 'en proceso':
          backgroundColor = '#4caf50';
          break;
        case 'creada':
          backgroundColor = '#ff9800';
          break;
        case 'cancelada':
          backgroundColor = '#f44336';
          break;
        default:
          backgroundColor = '#757575';
      }

      eventos.push({
        id: `contestacion-${audiencia.tramite_id}`,
        title: `⚖️ Vence contestación - ${audiencia.procurador_nombre}`,
        start: new Date(audiencia.fecha_maxima_contestacion),
        end: new Date(audiencia.fecha_maxima_contestacion),
        allDay: true,
        resource: {
          tipo: tipo,
          procurador: audiencia.procurador_nombre,
          procurador_id: audiencia.procurador_id,
          estado: audiencia.estado,
          tramite_id: audiencia.tramite_id,
          backgroundColor: backgroundColor
        }
      });
    });

    // Agregar demandas con filtro por rol
    if (demandasData && demandasData.length > 0) {
      demandasData.forEach(demanda => {
        // Si es LegalProcuradorTGU, solo mostrar sus propias demandas
        if (esLegalProcuradorTGU && demanda.procuradorid !== usuarioActualId) {
          return;
        }

        const fechasImportantes = [];

        // Fecha de presentación
        if (demanda.fechapresentacion) {
          fechasImportantes.push({
            fecha: demanda.fechapresentacion,
            tipo: 'presentacion',
            titulo: `📋 Presentación - ${demanda.numerodemanda || 'Demanda'}`,
            backgroundColor: '#2196f3'
          });
        }

        // Fecha máxima para contestación (si no está ya en audiencias)
        if (demanda.fechamaximacontestacion) {
          const existeEnAudiencias = datosFiltrados.some(
            aud => aud.tramite_id === demanda.tramiteid && aud.fecha_maxima_contestacion === demanda.fechamaximacontestacion
          );
          
          if (!existeEnAudiencias) {
            fechasImportantes.push({
              fecha: demanda.fechamaximacontestacion,
              tipo: 'contestacion',
              titulo: `⚖️ Vence contestación - ${demanda.numerodemanda || 'Demanda'}`,
              backgroundColor: '#ff9800'
            });
          }
        }

        // Crear eventos para cada fecha importante
        fechasImportantes.forEach((fechaInfo, index) => {
          let backgroundColor = fechaInfo.backgroundColor;
          
          // Asignar color según estado de la demanda
          switch(demanda.estado?.toLowerCase()) {
            case 'creada':
              backgroundColor = '#2196f3';
              break;
            case 'activa':
              backgroundColor = '#4caf50';
              break;
            case 'finalizada':
              backgroundColor = '#9e9e9e';
              break;
            case 'cancelada':
              backgroundColor = '#f44336';
              break;
          }

          eventos.push({
            id: `demanda-${demanda.tramiteid}-${fechaInfo.tipo}-${index}`,
            title: fechaInfo.titulo,
            start: new Date(fechaInfo.fecha),
            end: new Date(fechaInfo.fecha),
            allDay: true,
            resource: {
              tipo: 'demanda',
              subtipo: fechaInfo.tipo,
              tramite_id: demanda.tramiteid,
              numerodemanda: demanda.numerodemanda,
              numeroexpediente: demanda.numeroexpediente,
              nombredemandante: demanda.nombredemandante,
              nombredemandado: demanda.nombredemandado,
              procuradorasignado: demanda.procuradorasignado,
              procurador_id: demanda.procuradorid,
              estado: demanda.estado,
              backgroundColor: backgroundColor
            }
          });
        });

        // Si no hay fechas específicas, usar fecha de creación como respaldo
        if (fechasImportantes.length === 0 && demanda.fechacreacion) {
          const fechaCreacion = demanda.fechacreacion.split('T')[0];
          eventos.push({
            id: `demanda-${demanda.tramiteid}-creacion`,
            title: `📝 Creada - ${demanda.numerodemanda || 'Demanda'}`,
            start: new Date(fechaCreacion),
            end: new Date(fechaCreacion),
            allDay: true,
            resource: {
              tipo: 'demanda',
              subtipo: 'creacion',
              tramite_id: demanda.tramiteid,
              numerodemanda: demanda.numerodemanda,
              numeroexpediente: demanda.numeroexpediente,
              nombredemandante: demanda.nombredemandante,
              nombredemandado: demanda.nombredemandado,
              procuradorasignado: demanda.procuradorasignado,
              procurador_id: demanda.procuradorid,
              estado: demanda.estado,
              backgroundColor: '#9c27b0'
            }
          });
        }
      });
    }

    return eventos;
  };

  // Filtrar eventos por procurador
  const getFilteredCalendarEvents = () => {
    if (!filtroProcurador) {
      return calendarEvents;
    }
    
    return calendarEvents.filter(event => {
      // Para eventos de contestación (vienen de audiencias)
      if (event.resource?.tipo === 'contestacion') {
        return event.resource.procurador_id === filtroProcurador;
      }
      
      // Para eventos de demandas
      if (event.resource?.tipo === 'demanda') {
        return event.resource.procurador_id === filtroProcurador;
      }
      
      return true;
    });
  };

  // Manejar clic en evento del calendario
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleNavigateToDemanda = () => {
    if (selectedEvent) {
      const tramiteId = selectedEvent.resource.tramite_id;
      sessi.setVariable("demandaID", tramiteId);
      navigate(`/Legal/AccionesDemanda`);
      setDialogOpen(false);
    }
  };

  // Custom event component para mejor visualización
  const EventComponent = ({ event }) => {
    const getEventStyles = () => {
      const bgColor = event.resource?.backgroundColor || '#3174ad';
      return {
        backgroundColor: bgColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: '600',
        padding: '2px 4px',
      };
    };

    return (
      <div style={getEventStyles()}>
        <strong>{event.title}</strong>
      </div>
    );
  };

  // Fetch datos de audiencias
  useEffect(() => {
    const fetchAudienciasData = async () => {
      try {
        setLoading(true);
        const token = sessi.sessionData.token;
        const [audienciasRes, procuradoresRes] = await Promise.all([
          fetch('https://bei360.injupemp.gob.hn:8444/api/tramites/audiencia-proxima/', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          }),
          fetch('https://bei360.injupemp.gob.hn:8444/api/auth/oficialesLegal/', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          })
        ]);
        
        const [audienciasData, procuradoresData] = await Promise.all([
          audienciasRes.json(),
          procuradoresRes.json()
        ]);
        
        const procuradoresNormalized = normalizeProcuradores(procuradoresData);
        const audienciasProcessed = processAudienciasData(audienciasData, procuradoresNormalized);
        
        setAudiencias(audienciasProcessed);
        setProcuradores(procuradoresData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching audiencias data:', error);
        setLoading(false);
      }
    };
    
    fetchAudienciasData();
  }, [esLegalProcuradorTGU, usuarioActualId]);

  // Fetch datos de demandas cuando se cambia a vista calendario
  useEffect(() => {
    const fetchDemandasData = async () => {
      if (viewMode === 'calendario') {
        try {
          setLoadingDemandas(true);
          const token = sessi.sessionData.token;
          
          const demandasRes = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramite/listar/', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          });
          
          const demandasData = await demandasRes.json();
          setDemandas(demandasData);
          setLoadingDemandas(false);
        } catch (error) {
          console.error('Error fetching demandas data:', error);
          setLoadingDemandas(false);
        }
      }
    };
    
    fetchDemandasData();
  }, [viewMode, sessi.sessionData.token]);

  const getDatosFiltrados = () => {
    let datosProcesados = [];
    
    if (filtroProcurador) {
      const data = audiencias[filtroProcurador];
      if (data) {
        datosProcesados = data.audiencias.map(aud => ({
          ...aud,
          key: aud.tramite_id,
          procurador_id: filtroProcurador,
          procurador_nombre: data.procurador.nombre,
          procurador_email: data.procurador.email
        }));
      }
    } else {
      for (const [procuradorId, data] of Object.entries(audiencias)) {
        datosProcesados.push(...data.audiencias.map(aud => ({
          ...aud,
          key: aud.tramite_id,
          procurador_id: procuradorId,
          procurador_nombre: data.procurador.nombre,
          procurador_email: data.procurador.email
        })));
      }
    }
    
    return datosProcesados.sort((a, b) => 
      new Date(a.fecha_maxima_contestacion) - new Date(b.fecha_maxima_contestacion)
    );
  };

  const datosFiltrados = getDatosFiltrados();
  const calendarEvents = prepareCalendarEvents(datosFiltrados, demandas);
  const filteredCalendarEvents = getFilteredCalendarEvents();

  // Calcular datos paginados
  const datosPaginados = useMemo(() => {
    const startIndex = (paginaActual - 1) * elementosPorPagina;
    const endIndex = startIndex + elementosPorPagina;
    return datosFiltrados.slice(startIndex, endIndex);
  }, [datosFiltrados, paginaActual, elementosPorPagina]);

  const totalPaginas = Math.ceil(datosFiltrados.length / elementosPorPagina);

  const handleChangeProcurador = (e) => {
    setFiltroProcurador(e.target.value);
    setPaginaActual(1); // Resetear a primera página al cambiar filtro
  };

  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };

  const handleChangePagina = (event, value) => {
    setPaginaActual(value);
  };

  const handleChangeElementosPorPagina = (event) => {
    setElementosPorPagina(Number(event.target.value));
    setPaginaActual(1); // Resetear a primera página al cambiar tamaño
  };

  // Si es LegalProcuradorTGU, establecer automáticamente el filtro a su propio ID
  useEffect(() => {
    if (esLegalProcuradorTGU && usuarioActualId) {
      setFiltroProcurador(usuarioActualId);
    }
  }, [esLegalProcuradorTGU, usuarioActualId]);

  // Resetear paginación cuando cambien los datos filtrados
  useEffect(() => {
    setPaginaActual(1);
  }, [datosFiltrados.length]);

  return (
    <Box sx={{
      p: 3,
      maxWidth: 1200,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Box sx={{
        mb: 3,
        width: '100%',
        maxWidth: 1200
      }}>
        <Typography variant="h4" sx={{
          fontWeight: 600,
          fontSize: { xs: '1.5rem', sm: '2rem' },
          mb: 2,
          textAlign: 'center'
        }}>
          Próximas Demandas
        </Typography>

        {/* Selector de vista */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', my: 1 }}>
          <Tabs value={viewMode} onChange={handleViewModeChange} centered>
            <Tab label="Vista Lista" value="lista" />
            <Tab label="Vista Calendario" value="calendario" />
          </Tabs>
        </Box>

        {/* FILTRO GENERAL - SOLO VISIBLE PARA LEGALJEFE */}
        {esUsuarioLegalJefe && (
          <Box sx={{
            display: 'left',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FormControl sx={{
              minWidth: 250,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              width: '100%',
              maxWidth: 300
            }}>
              <InputLabel sx={{
                color: 'text.secondary',
                fontWeight: 500
              }}>
                Filtrar por procurador
              </InputLabel>
              <Select
                value={filtroProcurador}
                onChange={handleChangeProcurador}
                label="Filtrar por procurador"
                sx={{
                  '& .MuiSelect-select': {
                    py: 1.5,
                    fontWeight: 500
                  }
                }}
              >
                <MenuItem value="" sx={{ fontWeight: 500 }}>
                  Todos los procuradores
                </MenuItem>
                {procuradores.map(procurador => (
                  <MenuItem
                    key={procurador.id}
                    value={procurador.id}
                    sx={{ fontWeight: 500 }}
                  >
                    {procurador.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            height: 300,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
            width: '100%',
            maxWidth: 1000
          }}
        >
          <Box textAlign="center">
            <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Cargando datos...
            </Typography>
          </Box>
        </Box>
      ) : viewMode === 'lista' ? (
        // VISTA LISTA CON CARDS FLOTANTES Y PAGINACIÓN
        datosFiltrados.length === 0 ? (
          <Box
            sx={{
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center',
              boxShadow: 1,
              width: '100%',
              maxWidth: 1000
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              ⚖️ No hay fechas de contestación próximas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {filtroProcurador === ''
                ? 'No hay fechas de contestación para ningún procurador'
                : `No hay fechas de contestación para el procurador seleccionado`}
            </Typography>
          </Box>
        ) : (
          <Card variant="outlined" sx={{
            borderRadius: 2,
            boxShadow: 1,
            overflow: 'hidden',
            width: '100%',
            maxWidth: 1200
          }}>
            <Box sx={{
              backgroundColor: '#19438C',
              p: 2,
              color: 'white'
            }}>
              <Typography variant="h6" fontWeight={500} sx={{ textAlign: 'center' }}>
                {filtroProcurador === ''
                  ? 'Fechas por vencer'
                  : `Fechas por vencer ${audiencias[filtroProcurador]?.procurador.nombre || 'procurador seleccionado'}`}
                <Typography component="span" sx={{ ml: 1, opacity: 0.9 }}>
                  ({datosFiltrados.length} en total)
                </Typography>
              </Typography>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {/* Grid de Cards Flotantes */}
              <Box sx={{ 
                p: 2,
                display: 'grid',
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: 'repeat(2, 1fr)', 
                  md: 'repeat(3, 1fr)' 
                },
                gap: 2,
                minHeight: '400px'
              }}>
                {datosPaginados.map((audiencia, index) => (
                  <Card 
                    key={`${audiencia.tramite_id}-${index}`}
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      border: '1px solid #e0e0e0',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                        borderColor: '#19438C',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)'
                      }
                    }}
                    onClick={() => {
                      sessi.setVariable("demandaID", audiencia.tramite_id);
                      navigate(`/Legal/AccionesDemanda`);
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 2
                      }}
                    >

                      {/* Fecha Principal */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#1a2b3c',
                          fontSize: '1.15rem',
                          lineHeight: 1.3,
                          minHeight: '55px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {formatFecha(audiencia.fecha_maxima_contestacion)}
                      </Typography>

                      {/* Chip de días restantes */}
                      <Chip
                        label={calcularDiasRestantes(audiencia.fecha_maxima_contestacion)}
                        color={
                          calcularDiasRestantes(audiencia.fecha_maxima_contestacion).includes('Hoy') ||
                          calcularDiasRestantes(audiencia.fecha_maxima_contestacion).includes('Mañana')
                            ? 'error'
                            : 'warning'
                        }
                        size="medium"
                        sx={{
                          fontWeight: 700,
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          height: '32px',
                          px: 2,
                          alignSelf: 'center',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                        }}
                      />

                      {/* Información del Procurador */}
                      <Box
                        sx={{
                          backgroundColor: 'rgba(107, 197, 208, 0.18)',
                          borderRadius: 2,
                          py: 1.3,
                          px: 1.5,
                          border: '1px solid rgba(107, 197, 208, 0.35)',
                          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.05)'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#1a2b3c',
                            fontSize: '0.9rem'
                          }}
                        >
                          {audiencia.procurador_nombre}
                        </Typography>
                      </Box>

                      {/* ID del Trámite */}
                      <Box
                        sx={{
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          borderRadius: 2,
                          py: 1,
                          border: '1px solid rgba(0,0,0,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1.2,
                          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.04)'
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: '#19438C',
                            color: 'white',
                            borderRadius: '50%',
                            width: '22px',
                            height: '22px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                          }}
                        >
                          ID
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#555',
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                        >
                          {audiencia.tramite_id}
                        </Typography>
                      </Box>
                    </CardContent>

                  </Card>
                ))}
              </Box>

              {/* PAGINACIÓN MEJORADA */}
              {totalPaginas > 1 && (
                <Box sx={{ 
                  p: 3, 
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: 'rgba(107, 197, 208, 0.05)'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    {/* Información de página actual */}
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Mostrando {Math.min((paginaActual - 1) * elementosPorPagina + 1, datosFiltrados.length)}-
                      {Math.min(paginaActual * elementosPorPagina, datosFiltrados.length)} de {datosFiltrados.length} elementos
                    </Typography>

                    {/* Paginación de Material-UI */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Por página</InputLabel>
                        <Select
                          value={elementosPorPagina}
                          label="Por página"
                          onChange={handleChangeElementosPorPagina}
                        >
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={6}>6</MenuItem>
                          <MenuItem value={9}>9</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <Pagination
                        count={totalPaginas}
                        page={paginaActual}
                        onChange={handleChangePagina}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontWeight: 600,
                            borderRadius: 2
                          },
                          '& .MuiPaginationItem-page.Mui-selected': {
                            backgroundColor: '#19438C',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#5ab4c0'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )
      ) : (
        /* === VISTA CALENDARIO CON REACT BIG CALENDAR === */
        <Card 
          sx={{ 
            width: '100%', 
            maxWidth: 1200,
            borderRadius: 3,
            boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
            background: "white",
            overflow: "hidden",
            border: "1px solid #e2e8f0"
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {loadingDemandas ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                sx={{ height: 400 }}
              >
                <CircularProgress size={50} sx={{ color: '#19438C' }} />
                <Typography variant="h6" sx={{ ml: 2, color: "#64748b", fontWeight: 500 }}>
                  Cargando calendario...
                </Typography>
              </Box>
            ) : (
              <>
                {/* HEADER MEJORADO PARA CALENDARIO */}
                <Box sx={{ 
                  mb: 4, 
                  textAlign: 'center',
                  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0"
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 2
                    }}
                  >
                    Calendario de Fechas Críticas
                    {filtroProcurador && (
                      <Typography component="span" variant="body1" sx={{ ml: 1, color: '#19438C', fontWeight: 600 }}>
                        - {procuradores.find(p => p.id === filtroProcurador)?.nombre}
                      </Typography>
                    )}
                  </Typography>
                  
                  {/* LEYENDA MEJORADA */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap', 
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: '#FF6B35',
                        boxShadow: "0 2px 4px rgba(255,107,53,0.3)"
                      }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                        Vence Contestación
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: '#4D96FF',
                        boxShadow: "0 2px 4px rgba(77,150,255,0.3)"
                      }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                        Presentación Demanda
                      </Typography>
                    </Box>
                   </Box> 
                  </Box>
                
                {/* CALENDARIO REACT BIG CALENDAR */}
                <Box
                  sx={{
                    background: "white",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    width: "100%",
                    height: "600px",
                    minHeight: "600px",
                    '& .rbc-calendar': {
                      height: '100%',
                      fontFamily: "'Inter', 'Roboto', sans-serif"
                    },
                    '& .rbc-header': {
                      background: '#f8fafc',
                      padding: '12px 8px',
                      fontWeight: 600,
                      color: '#475569',
                      borderColor: '#e2e8f0'
                    },
                    '& .rbc-month-view': {
                      borderColor: '#e2e8f0'
                    },
                    '& .rbc-day-bg': {
                      borderColor: '#f1f5f9'
                    },
                    '& .rbc-today': {
                      backgroundColor: 'rgba(107, 197, 208, 0.1)'
                    },
                    '& .rbc-event': {
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }
                  }}
                >
                  <Calendar
                    localizer={localizer}
                    events={filteredCalendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    onSelectEvent={handleEventClick}
                    views={['month', 'week', 'day', 'agenda']}
                    defaultView="month"
                    popup
                    style={{ height: '100%' }}
                    messages={{
                      next: "Siguiente",
                      previous: "Anterior",
                      today: "Hoy",
                      month: "Mes",
                      week: "Semana",
                      day: "Día",
                      agenda: "Agenda",
                      date: "Fecha",
                      time: "Hora",
                      event: "Evento",
                      noEventsInRange: "No hay eventos en este rango"
                    }}
                    components={{
                      event: EventComponent
                    }}
                  />
                </Box>

                {/* MENSAJE CUANDO NO HAY EVENTOS FILTRADOS */}
                {filteredCalendarEvents.length === 0 && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 4, 
                    textAlign: 'center',
                    backgroundColor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      📅 No hay eventos para mostrar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filtroProcurador 
                        ? `No hay eventos para el procurador "${procuradores.find(p => p.id === filtroProcurador)?.nombre}"`
                        : 'No hay eventos en el calendario'
                      }
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* DIALOG PARA DETALLES DEL EVENTO */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Detalles del Evento
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedEvent.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Fecha:</strong> {selectedEvent.start.toLocaleDateString('es-HN')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Tipo:</strong> {selectedEvent.resource?.tipo === 'contestacion' ? 'Vence Contestación' : 
                                      selectedEvent.resource?.subtipo === 'presentacion' ? 'Presentación Demanda' : 'Evento'}
              </Typography>
              {selectedEvent.resource?.procurador && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Procurador:</strong> {selectedEvent.resource.procurador}
                </Typography>
              )}
              {selectedEvent.resource?.numerodemanda && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>N° Demanda:</strong> {selectedEvent.resource.numerodemanda}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                <strong>ID Trámite:</strong> {selectedEvent.resource?.tramite_id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}
            color='error'>
            Cerrar
          </Button>
          <Button 
            onClick={handleNavigateToDemanda}
            variant="contained"
            sx={{ backgroundColor: '#19438C' }}
          >
            Ir a la Demanda
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProximasAudiencias;